import BaseService from './BaseService';
import uuid from 'uuid/v4';
import { DRNUtil } from '@traveloka/district-util';

import { RESPONSE_TYPE } from './constants';

export default class BaseDRNService extends BaseService {
  timeout = 10000; // in miliseconds
  retry = 0;

  constructor(store) {
    super(store);
    this.store = store;
  }

  /**
   * add nonce in header
   * @param {object} header - request headers (key-value object)
   * @param {string} nonce - string unique to identify matching request and response
   * @returns {object} object for header
   */
  getHeader(header = {}, nonce) {
    return {
      Origin: 'm.traveloka.com',
      ...header,
      'x-nonce': nonce,
    };
  }

  /**
   * can override to add additional context variable. Useful if you want to integrate with redux store.
   * @param {object} context
   * @returns {object} context object
   */
  getContext(context = {}) {
    return context;
  }

  /**
   * populate context, and clientInterface in data before pass into fetch.
   * @param {object} data - additional data
   * @param {array} fields
   * @param {string} nonce - string unique to identify matching request and response
   * @returns {object} data
   */
  getData(data = {}, fields = [], nonce) {
    const context = data.context || {};
    return {
      clientInterface: DRNUtil.isiOS() ? 'mobile-ios' : 'mobile-android',
      fields,
      data,
      context: this.getContext({
        nonce,
        ...context,
      }),
    };
  }

  /**
   * validate if nonce is different from request and response.
   * @param {object} fetchConfig - contain url, nonce, responseType, method.
   * @param {object} res - response from server
   * @returns {object} response data
   */
  handleSuccess({ url, nonce, responseType, method }, res = {}) {
    // Validate nonce
    if (res.headers && res.headers['x-nonce'] && res.headers['x-nonce'] !== nonce) {
      throw new Error(`[Service]: Invalid Nonce Header ${url}, ${nonce} ${res.headers['x-nonce']}`);
    }
    const result = res.data ? res.data : res;
    // Validate and return response
    if (responseType === RESPONSE_TYPE.JSON) {
      const responseData = result;
      if (
        method === 'post' &&
        responseData.context &&
        responseData.context.nonce !== undefined &&
        responseData.context.nonce !== nonce
      ) {
        throw new Error(`[Service]: Invalid Nonce ${url}, ${nonce} ${responseData.context.nonce}`);
      }
      return responseData;
    }
    return result;
  }

  /**
   * construct axios config
   * @param {string} method - get / post
   * @param {string} path - postfix url, don't pass with base url
   * @param {object} data - object that will be pass
   * @param {object} header - request headers (key-value object)
   * @param {array} fields
   * @returns {Promise}
   */
  constructConfig(_method, path, data = {}, header = {}, fields = []) {
    const nonce = uuid();
    const method = _method.toLowerCase();
    const config = {
      baseUrl: this.getBaseUrl(),
      path,
      method,
      headers: this.getHeader(header, nonce),
      data: method === 'post' ? this.getData(data, fields, nonce) : null,
      params: method === 'get' ? data : null,
      responseType: 'json',
      nonce,
    };
    return config;
  }

  fetch(...args) {
    const config = this.constructConfig(...args);
    return super.fetch(config);
  }

  getBaseUrl() {
    if (this.isStaging()) return this.getStagingUrl();
    return this.getProductionUrl();
  }

  /**
   * @return {boolean} - return true if in staging environment
   */
  isStaging() {
    throw new Error('Must implement isStaging function.');
  }

  /**
   * @return {string} - return base staging url
   */
  getStagingUrl() {
    throw new Error('Must implement getStagingUrl function.');
  }

  /**
   * @return {string} - return base production url
   */
  getProductionUrl() {
    throw new Error('Must implement getProductionUrl function.');
  }
}
